/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * The incoming-call ringtone.
 *
 * This app had no audio code at all before the call center, so every
 * browser-audio quirk lives here rather than being rediscovered in a
 * component:
 *
 * - Chrome blocks audio until the user has interacted with the document. Going
 *   "Available" is a click, so `prime()` is called from that handler to unlock
 *   playback before the first call ever arrives.
 * - The element is a module singleton. One per render would leave orphaned
 *   audio playing after a re-render, and the ring would never stop.
 * - Twilio's own ringtone is disabled in the Device config so there is exactly
 *   one source of ringing to reason about.
 *
 * Falls back to a synthesised tone: shipping without an asset is better than a
 * silent softphone, and it means the ring works before anyone adds an mp3.
 */

const RING_SRC = "/sounds/ring.wav";

let element = null;
let audioContext = null;
let oscillatorTimer = null;
let unlocked = false;

function getElement() {
  if (element) return element;
  if (typeof Audio === "undefined") return null;
  element = new Audio(RING_SRC);
  element.loop = true;
  element.preload = "auto";
  // Ringing at full volume through headphones is hostile.
  element.volume = 0.5;
  return element;
}

/**
 * Synthesised ring, used when the asset cannot play.
 *
 * Scheduled on the AudioContext clock, NOT with setInterval. Browsers clamp
 * timers in a background tab to once per second and Chrome goes further still
 * for hidden tabs, so the previous `setInterval(beep, 2000)` degraded to
 * roughly one beep a minute in exactly the situation this is for: the agent is
 * looking at another tab and needs to hear the phone. Work queued on the audio
 * clock is not subject to that clamping, so this schedules a batch of rings
 * ahead and tops it up.
 */
const RING_PERIOD = 4;    // seconds, matching the asset's cadence
const SCHEDULE_AHEAD = 3; // rings queued at a time

let scheduledUntil = 0;
let topUpTimer = null;
let scheduledNodes = [];

function scheduleBurst(at) {
  // Two short bursts, the same shape as the asset, so the fallback does not
  // sound like a different product.
  [0, 0.6].forEach((offset) => {
    [400, 450].forEach((freq) => {
      const osc = audioContext.createOscillator();
      const gain = audioContext.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      const startAt = at + offset;
      // Ramped rather than switched: a square gain edge clicks.
      gain.gain.setValueAtTime(0, startAt);
      gain.gain.linearRampToValueAtTime(0.06, startAt + 0.02);
      gain.gain.setValueAtTime(0.06, startAt + 0.38);
      gain.gain.linearRampToValueAtTime(0, startAt + 0.4);
      osc.connect(gain).connect(audioContext.destination);
      osc.start(startAt);
      osc.stop(startAt + 0.42);
      scheduledNodes.push(osc);
    });
  });
}

function pump() {
  if (!audioContext) return;
  const now = audioContext.currentTime;
  if (scheduledUntil < now) scheduledUntil = now;
  while (scheduledUntil < now + SCHEDULE_AHEAD * RING_PERIOD) {
    scheduleBurst(scheduledUntil);
    scheduledUntil += RING_PERIOD;
  }
}

function startFallback() {
  if (typeof window === "undefined" || !window.AudioContext) return;
  stopFallback();
  try {
    audioContext = audioContext || new window.AudioContext();
    if (audioContext.state === "suspended") audioContext.resume();
    scheduledUntil = audioContext.currentTime;
    pump();
    // The top-up interval is itself throttled in a background tab, which is
    // fine: it only has to run once per SCHEDULE_AHEAD window, and the already
    // queued rings keep sounding meanwhile.
    topUpTimer = setInterval(pump, RING_PERIOD * 1000);
  } catch {
    /* audio unavailable; the visual ring indicator still shows */
  }
}

function stopFallback() {
  if (topUpTimer) {
    clearInterval(topUpTimer);
    topUpTimer = null;
  }
  if (oscillatorTimer) {
    clearInterval(oscillatorTimer);
    oscillatorTimer = null;
  }
  // Queued nodes outlive the call unless they are explicitly stopped -- up to
  // three rings would play on after the agent answered.
  scheduledNodes.forEach((node) => {
    try {
      node.stop();
    } catch {
      /* already stopped */
    }
  });
  scheduledNodes = [];
  scheduledUntil = 0;
}

/**
 * Unlock audio from inside a user gesture.
 *
 * Plays and immediately pauses, which satisfies the autoplay policy without
 * the user hearing anything.
 */
export function prime() {
  const el = getElement();
  if (!el || unlocked) return;
  el.play()
    .then(() => {
      el.pause();
      el.currentTime = 0;
      unlocked = true;
    })
    .catch(() => {
      /* still locked; start() will fall back to the oscillator */
    });
  if (typeof window !== "undefined" && window.AudioContext && !audioContext) {
    try {
      audioContext = new window.AudioContext();
    } catch {
      /* ignore */
    }
  }
}

export function start() {
  const el = getElement();
  if (!el) {
    startFallback();
    return;
  }
  el.currentTime = 0;
  const played = el.play();
  if (played && typeof played.catch === "function") {
    // Missing asset or still-locked audio: fall back rather than ring silently.
    played.catch(() => startFallback());
  }
}

export function stop() {
  stopFallback();
  if (!element) return;
  try {
    element.pause();
    element.currentTime = 0;
  } catch {
    /* ignore */
  }
}

export function isUnlocked() {
  return unlocked;
}
